import { useEffect, useRef, useState } from "react";
import { Upload, Save, FileText, Loader2, ImagePlus, Trash2, RefreshCw } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader, Panel, BrandButton } from "@/components/PageHeader";
import { CertCanvas } from "@/components/certificates/CertCanvas";
import { TextBoxControls, QrControls } from "@/components/certificates/BoxControls";
import { api, errMsg } from "@/lib/api";

const BOXES = [["name_box", "Participant name"], ["serial_box", "Serial number"], ["qr_box", "Verification QR"]];
const suggestCode = (title = "") => {
  const words = title.split(/\s+/).filter((w) => /^[A-Za-z]/.test(w));
  return (words.map((w) => w[0]).join("").toUpperCase().slice(0, 6) + (title.match(/\d{2}(?=\D*$)/)?.[0] || "")).slice(0, 12);
};

const Uploader = ({ onDone, replace }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = async (file) => {
    if (!file) { return; }
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try { onDone((await api.post("/uploads/certificate", fd)).data); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); ref.current.value = ""; }
  };
  return <>
    <input ref={ref} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => pick(e.target.files[0])} data-testid="cert-upload-input" />
    <BrandButton variant="outline" className="w-full" onClick={() => ref.current.click()} disabled={busy} data-testid="cert-upload-button">
      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : replace ? <RefreshCw className="w-4 h-4" /> : <Upload className="w-4 h-4" />}{replace ? "Replace design, keep positions" : "Upload certificate design"}
    </BrandButton>
  </>;
};

const Empty = () => (
  <div className="aspect-[1.414/1] w-full border-2 border-dashed border-gray-300 rounded-lg grid place-items-center text-center p-6 bg-white" data-testid="cert-empty">
    <div><ImagePlus className="w-10 h-10 mx-auto text-gray-400" /><div className="mt-2 font-heading font-bold">Upload the full certificate design</div>
      <div className="text-sm text-gray-500 mt-1 max-w-sm">A PNG or JPG of the finished certificate with a blank space for the name, serial number and QR code.</div></div>
  </div>
);

export default function Certificates() {
  const [events, setEvents] = useState(null);
  const [eventId, setEventId] = useState();
  const [t, setT] = useState(null);
  const [sel, setSel] = useState("name_box");
  const [busy, setBusy] = useState("");
  const [askReset, setAskReset] = useState(false);
  const reset = async () => {
    setAskReset(false);
    setBusy("reset");
    try {
      const r = (await api.delete(`/cert-templates/${eventId}`)).data;
      setT(r); setSel("name_box");
      toast.success(r.issued ? `Template removed. Event code ${r.code} is kept because ${r.issued} certificate(s) were already issued` : "Template removed");
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(""); }
  };
  const ev = events?.find((e) => e.id === eventId);
  useEffect(() => {
    api.get("/cert-templates").then((r) => { setEvents(r.data); setEventId(r.data[0]?.id); }).catch(() => setEvents([]));
  }, []);
  useEffect(() => {
    if (!eventId) { return; }
    setT(null);
    api.get(`/cert-templates/${eventId}`).then((r) => setT(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [eventId]);
  const setBox = (key, patch) => setT((x) => ({ ...x, [key]: { ...x[key], ...patch } }));
  const swapDesign = (f) => {
    const had = t.image_url && t.width;
    const shift = had ? Math.abs(f.width / f.height - t.width / t.height) / (t.width / t.height) : 0;
    setT((x) => ({ ...x, image_url: f.url, width: f.width, height: f.height }));
    if (!had) { toast.success("Certificate design uploaded"); }
    else if (shift > 0.03) { toast.warning("New design uploaded with your box positions kept. Its shape differs from the old one, so check the boxes before saving."); }
    else { toast.success("New design uploaded with your box positions kept. Press Save to use it."); }
  };
  const body = () => ({ ...t, code: t.code || suggestCode(ev?.title) });
  const save = async () => {
    setBusy("save");
    try { setT((await api.put(`/cert-templates/${eventId}`, body())).data); toast.success("Certificate template saved for this event"); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(""); }
  };
  const preview = async () => {
    setBusy("preview");
    try { const r = await api.post(`/cert-templates/${eventId}/preview`, body(), { responseType: "blob" }); window.open(URL.createObjectURL(r.data), "_blank"); }
    catch (e) { toast.error(e.response?.status === 422 ? "Enter an event code of 2–12 letters or numbers" : "Could not build the preview"); }
    finally { setBusy(""); }
  };
  return (
    <div data-testid="certificates-page">
      <PageHeader eyebrow="Control" title="Certificate Studio" description="Upload each event's certificate design and place the participant name, serial number and verification QR code on it. Drag the boxes to position them." />
      <div className="grid grid-cols-1 xl:grid-cols-[320px_1fr] gap-4 items-start">
        <Panel title="Template">
          <div className="space-y-5">
            <div>
              <div className="text-xs font-bold uppercase tracking-wider text-gray-600 mb-1.5">Event</div>
              <Select value={eventId} onValueChange={setEventId}>
                <SelectTrigger className="h-10" data-testid="cert-event-select"><SelectValue placeholder="Choose an event" /></SelectTrigger>
                <SelectContent>{(events || []).map((e) => <SelectItem key={e.id} value={e.id}>{e.title}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {t && <>
              <Uploader replace={!!t.image_url} onDone={swapDesign} />
              <div>
                <div className="text-xs font-bold uppercase tracking-wider text-gray-600 mb-1.5">Event code for serial numbers</div>
                <Input value={t.code} onChange={(e) => setT({ ...t, code: e.target.value.replace(/[^A-Za-z0-9]/g, "").toUpperCase().slice(0, 12) })} placeholder={suggestCode(ev?.title) || "AGM26"} className="h-10 font-mono" data-testid="cert-code-input" />
                <div className="text-xs text-gray-500 mt-1">Serials look like <span className="font-mono">{(t.code || suggestCode(ev?.title) || "CODE")}-0001</span>, numbered in the order certificates are issued.</div>
              </div>
              {t.image_url && <>
                <div className="flex flex-col gap-1">{BOXES.map(([k, l]) => (
                  <button key={k} onClick={() => setSel(k)} data-testid={`cert-select-${k}`} className={`text-left text-sm px-3 h-9 rounded-md border transition-colors ${sel === k ? "border-[#E60000] bg-red-50 font-semibold" : "border-[#E5E7EB] hover:border-gray-400"}`}>{l}</button>
                ))}</div>
                {sel === "qr_box" ? <QrControls b={t.qr_box} set={(p) => setBox("qr_box", p)} /> : <TextBoxControls b={t[sel]} set={(p) => setBox(sel, p)} prefix={`cert-${sel.split("_")[0]}`} />}
                <div className="grid grid-cols-2 gap-2">
                  <BrandButton variant="outline" onClick={preview} disabled={!!busy} data-testid="cert-preview-button">{busy === "preview" ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}Preview PDF</BrandButton>
                  <BrandButton onClick={save} disabled={!!busy} data-testid="cert-save-button">{busy === "save" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}Save</BrandButton>
                </div>
                <button type="button" onClick={() => setAskReset(true)} disabled={!!busy} className="w-full inline-flex items-center justify-center gap-2 h-9 rounded-md text-sm font-semibold text-[#B30000] hover:bg-red-50 transition-colors disabled:opacity-50" data-testid="cert-reset-button">
                  {busy === "reset" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}Remove template / reset
                </button>
              </>}
            </>}
          </div>
        </Panel>
        <div>{events && !events.length ? <div className="p-10 text-center text-sm text-gray-500 bg-white border rounded-lg" data-testid="cert-no-events">Create an event in Event Manager Studio first.</div>
          : !t ? <div className="text-gray-500">Loading…</div> : t.image_url ? <CertCanvas t={t} selected={sel} onSelect={setSel} onMove={setBox} /> : <Empty />}</div>
      </div>
      <AlertDialog open={askReset} onOpenChange={setAskReset}>
        <AlertDialogContent data-testid="cert-reset-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this event's certificate template?</AlertDialogTitle>
            <AlertDialogDescription>The uploaded design and box positions will be cleared and set back to the defaults. Certificates can't be sent, and already-sent certificate download links won't open, until you upload and save a new design. Serial numbers already issued stay the same.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cert-reset-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={reset} className="bg-[#E60000] hover:bg-[#CC0000]" data-testid="cert-reset-confirm">Remove template</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
